import React, { Fragment, useEffect, useMemo, useState } from "react";
import PageTitle from "../../layouts/PageTitle";
import {
  Row,
  Col,
  Card,
  Table,
  Form,
  InputGroup,
  Button,
  Modal,
  Badge,
  Alert,
} from "react-bootstrap";

const BASE_URL = "https://admin.vascularirdoctors.com/backend";

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [doctorFilter, setDoctorFilter] = useState("");

  const [alert, setAlert] = useState({
    show: false,
    type: "",
    text: "",
  });

  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // ALERT FUNCTION
  const showAlert = (type, text) => {
    setAlert({ show: true, type, text });
    setTimeout(() => {
      setAlert({ show: false, type: "", text: "" });
    }, 3000);
  };

  // FETCH APPOINTMENTS
  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/appointments`);
      const data = await res.json();
      setAppointments(data);
    } catch (err) {
      console.log("FETCH APPOINTMENTS ERROR:", err);
      showAlert("danger", "Unable to fetch appointments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  // UNIQUE DOCTORS FOR FILTER
  const uniqueDoctors = useMemo(() => {
    const names = appointments.map((item) => item.doctor_name).filter(Boolean);
    return [...new Set(names)];
  }, [appointments]);

  // FILTERED DATA
  const filteredAppointments = useMemo(() => {
    return appointments.filter((item) => {
      const matchesSearch =
        item.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
        item.email?.toLowerCase().includes(search.toLowerCase()) ||
        item.phone?.toLowerCase().includes(search.toLowerCase()) ||
        item.doctor_name?.toLowerCase().includes(search.toLowerCase()) ||
        item.service?.toLowerCase().includes(search.toLowerCase());

      const matchesDoctor = doctorFilter
        ? item.doctor_name === doctorFilter
        : true;

      return matchesSearch && matchesDoctor;
    });
  }, [appointments, search, doctorFilter]);

  // DELETE
  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const deleteAppointment = async () => {
    try {
      const res = await fetch(`${BASE_URL}/appointments/${deleteId}`, {
        method: "DELETE",
      });

      const result = await res.json();

      if (res.ok) {
        showAlert("success", "Appointment deleted successfully.");
        fetchAppointments();
      } else {
        showAlert("danger", result.message || "Delete failed.");
      }
    } catch (err) {
      console.log("DELETE APPOINTMENT ERROR:", err);
      showAlert("danger", "Unable to delete appointment.");
    }

    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCreatedDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Fragment>
      <PageTitle activeMenu="Appointments" motherMenu="Dashboard" />

      <Row>
        <Col lg={12}>
          {alert.show && (
            <Alert variant={alert.type} className="shadow-sm rounded-3">
              {alert.text}
            </Alert>
          )}

          <Card className="shadow-sm border-0">
            <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <Card.Title className="mb-0">Appointments List</Card.Title>

              <div className="d-flex flex-wrap gap-2">
                <InputGroup style={{ minWidth: "260px" }}>
                  <Form.Control
                    type="text"
                    placeholder="Search patient, doctor, phone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </InputGroup>

                <Form.Select
                  value={doctorFilter}
                  onChange={(e) => setDoctorFilter(e.target.value)}
                  style={{ minWidth: "220px" }}
                >
                  <option value="">All Doctors</option>
                  {uniqueDoctors.map((doctor, index) => (
                    <option key={index} value={doctor}>
                      {doctor}
                    </option>
                  ))}
                </Form.Select>

                <Button variant="outline-primary" onClick={fetchAppointments}>
                  Refresh
                </Button>
              </div>
            </Card.Header>

            <Card.Body>
              <div className="mb-3 d-flex flex-wrap gap-3">
                <Badge bg="primary" className="px-3 py-2 fs-6">
                  Total: {appointments.length}
                </Badge>

                <Badge bg="success" className="px-3 py-2 fs-6">
                  Showing: {filteredAppointments.length}
                </Badge>
              </div>

              <div className="table-responsive">
                <Table
                  responsive
                  bordered
                  striped
                  hover
                  className="align-middle text-nowrap"
                >
                  <thead className="table-light">
                    <tr>
                      <th>S/N</th>
                      <th>Patient Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th>Doctor</th>
                      <th>Service</th>
                      <th>Appointment Date</th>
                      <th>Time</th>
                      <th>Message</th>
                      <th>Booked On</th>
                      <th style={{ minWidth: "110px" }}>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="12" className="text-center py-4">
                          Loading appointments...
                        </td>
                      </tr>
                    ) : filteredAppointments.length > 0 ? (
                      filteredAppointments.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td>{item.patient_name || "-"}</td>
                          <td>{item.email || "-"}</td>
                          <td>{item.phone || "-"}</td>
                          <td style={{ whiteSpace: "normal", minWidth: "180px" }}>
                            {item.address || "-"}
                          </td>
                          <td>{item.doctor_name || "-"}</td>
                          <td>{item.service || "-"}</td>
                          <td>{formatDate(item.appointment_date)}</td>
                          <td>{item.appointment_time || "-"}</td>
                          <td style={{ whiteSpace: "normal", minWidth: "220px" }}>
                            {item.message || "-"}
                          </td>
                          <td>{formatCreatedDate(item.createdAt)}</td>
                          <td>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => confirmDelete(item.id)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="12" className="text-center py-4">
                          No Appointments Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* DELETE MODAL */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Appointment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this appointment? This action cannot
          be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteAppointment}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default Appointments;